import { TeamResource } from 'dy-frontend-http-repository/lib/modules/Team/resources';
import { TeamOccupancyFilterInput } from 'dy-frontend-http-repository/lib/modules/TeamOccupancy/inputs';
import repository from 'dy-frontend-http-repository/lib/repository';

import { TeamOccupancyWithParticipantsResource } from '@pages/Team/types';

import { TeamHealthPeriod, isPeriodEcho } from '../utils/teamHealth';

// Local widening of the shared repository types for the additive period parameters
// NOTE: the shared package is not changed; the existing client serializes every filter key it is handed
export type TeamOccupancyPeriodFilterInput = TeamOccupancyFilterInput & { from_date: string; to_date: string };

export interface TeamPeriodOccupancyResource extends TeamOccupancyWithParticipantsResource {
    from_date?: string;
    to_date?: string;
}

export class TeamOccupancyPeriodMismatchError extends Error {}

// Every active team, with its active participants
export const fetchActiveTeams = async (): Promise<TeamResource[]> => {
    const teams = await repository.team().get({ is_archived: '0' });

    return teams.items;
};

// Occupancy of the given teams over a period
// NOTE: a reply that does not echo the requested period (e.g. from a backend without period support,
// NOTE: which would silently answer for the current week) is refused rather than rendered
export const fetchTeamPeriodOccupancy = async (
    teamIds: string[],
    period: TeamHealthPeriod
): Promise<TeamPeriodOccupancyResource[]> => {
    if (teamIds.length === 0) {
        return [];
    }

    const filter: TeamOccupancyPeriodFilterInput = {
        team_id: teamIds,
        from_date: period.fromDate,
        to_date: period.toDate,
    };
    const response = await repository.teamOccupancy().get(filter);
    const items = response.items as TeamPeriodOccupancyResource[];

    if (!isPeriodEcho(items, period)) {
        throw new TeamOccupancyPeriodMismatchError(
            `Team occupancy did not answer for ${period.fromDate} – ${period.toDate}`
        );
    }

    return items;
};

// Runs period requests a few at a time so a year of weeks does not fire all at once
export const fetchTeamPeriodOccupancyList = async (
    teamIds: string[],
    periods: TeamHealthPeriod[],
    concurrency = 4
): Promise<TeamPeriodOccupancyResource[][]> => {
    const results: TeamPeriodOccupancyResource[][] = [];
    for (let index = 0; index < periods.length; index += concurrency) {
        const batch = periods.slice(index, index + concurrency);
        results.push(...(await Promise.all(batch.map((period) => fetchTeamPeriodOccupancy(teamIds, period)))));
    }

    return results;
};
